import { User } from 'mission-control-common-components';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { AdSortOptionV5, CampaignSortOptionV5, LineItemSortOptionV5, TraffickingTab } from '../../../../apis/graphql';
import type { ModalProps } from '../../../../common/CustomModal/useModalProps';
import useModalProps from '../../../../common/CustomModal/useModalProps';
import type { DrawerProps } from '../../../../common/Drawer/useDrawerProps';
import useDrawerProps from '../../../../common/Drawer/useDrawerProps';
import type { PermissionsNames } from '../../../../constants';
import { TraffickingTableName } from '../../../../constants';
import type { GlobalFilters } from '../../../../contexts/FilterContext';
import { useFiltersContext } from '../../../../contexts/FilterContext';
import { getStringOperatorFilterKeys } from '../../../../contexts/FilterContext/hooks/utils';
import { useFeatureFlags } from '../../../../hooks/useFeatureFlags';
import { ResultKeys } from '../../constants';
import { CampaignColumn } from '../../constants/campaign';
import { LineItemColumn } from '../../constants/lineItems';
import { useGetTraffickingFilterConfig } from '../../TableFilterBar/AdditionalFilters/hooks';
import type { FilterOptionsInput } from '../../utils';
import {
  generateQueryVariables,
  getAllowedFilterKeys,
  getConfigDrivenFilterVars,
  getEffectiveDateFilter,
  getSelectedRowIdsFromState,
} from '../../utils';
import { getAdsColumns, getCampaignsColumns, getLineItemsColumns } from '../columns';
import type { AdModel, AdNode, CampaignModel, CampaignNode, LineItemModel, LineItemNode } from '../modelConverters';
import { adNodeToModel, campaignNodeToModel, lineItemNodeToModel } from '../modelConverters';
import type { TraffickingPageDrawerData } from './makePageDrawerPlugin';
import {
  GET_ADS,
  GET_CAMPAIGNS,
  GET_LINEITEMS_BY_CAMPAIGNID_LIST,
  GET_LINEITEMS_BY_CAMPAIGNID_LIST_WITH_HEALTH,
} from './queries';
import type { SelectedRowIds } from './types';
import { useAssociatedEntities } from './useAssociatedEntities';
import useCascadingDeselection from './useCascadingDeselection';
import useCascadingSelection from './useCascadingSelection';
import { useGetUnacknowledgedCampaignIds } from './useGetUnacknowledgedCampaignIds';
import useOpenDrawerOnNavigation from './useOpenDrawerOnNavigation';
import useShowSelectedOnly from './useShowSelectedOnly';
import type { TraffickerTableState } from './useTraffickerTable';
import useTraffickerTable from './useTraffickerTable';
import useTraffickingTableAutoReload from './useTraffickingTableAutoReload';
import {
  clearTableSearchResults,
  clearTablesSelections,
  determineClearAllBadgeVisibility,
  getDeselectedEntities,
  getDifferenceFromSelectedEntities,
  getRepresentedInSelectedEntities,
  getSelectedEntities,
  getUnselectedIds,
  hasSelectedRowIds,
  updateOffsetsBasedOnSelectedTab,
} from './utils';

// Re-exported so existing importers (and the hooks barrel) keep resolving it from here.
// It now lives in ./types so the cascade hooks can take it without importing this module.
export type { SelectedRowIds };

export interface TraffickerTableStates {
  campaigns: TraffickerTableState<CampaignModel>;
  lineItems: TraffickerTableState<LineItemModel>;
  ads: TraffickerTableState<AdModel>;
}

export interface TraffickerState {
  drawerProps: DrawerProps<TraffickingPageDrawerData>;
  modalProps: ModalProps;
  tables: TraffickerTableStates;
  applyCommonFilter: <T extends keyof GlobalFilters>(filterName: T, filterValue: GlobalFilters[T]) => void;
  isClearAllBadgeVisible: boolean;
  clearAllSearchResultsAndSelections: () => void;
  clearAdsIdsToQuery: () => void;
  isFilterPanelOpen: boolean;
  scheduleTableReload: () => void;
  /** True while a post-mutation reload is pending (delay) or in flight (refetch). */
  isReloading: boolean;
  openFilterPanel: () => void;
  closeFilterPanel: () => void;
  scrollToFilterId: string | null;
  openFilterPanelToFilter: (filterId: string) => void;
  clearScrollToFilterId: () => void;
  lineItemNavigationScroll: {
    scrollTargetId: string | null;
    clearScrollTarget: () => void;
  };
  /** "Show selected only" — one value shared by all three tabs. */
  showSelectedOnly: {
    isEnabled: boolean;
    isAvailable: boolean;
    setIsEnabled: (value: boolean) => void;
  };
}

export interface QueryVariables {
  /** All filter fields packed as a single filterOptions object sent to the GQL query. */
  filterOptions: FilterOptionsInput;
  sort?: string;
  order?: string;
  limit?: number;
  offset?: number;
  [key: string]: unknown;
}

type SortOptions = Record<string, CampaignSortOptionV5 | LineItemSortOptionV5 | AdSortOptionV5>;

export const sortOptionMap: Record<TraffickingTableName, SortOptions> = {
  campaigns: CampaignSortOptionV5,
  lineItems: LineItemSortOptionV5,
  ads: AdSortOptionV5,
};

function useTraffickerState(): TraffickerState {
  const permissions = User.getPermissions<PermissionsNames>();
  const { filters, applyTableFilter, applyFilter, clearTableFilters, selectedCustomFilter } = useFiltersContext();
  const {
    adsPacingStatusAlignment,
    adsPacingStatusAlignmentDefaultHide,
    adsVsmCampaignInvestigatorUi,
    adsVsmLineItemNavigationLinks,
    traffickingNotifications,
  } = useFeatureFlags();

  const [campaignIdsToQuery, setCampaignIdsToQuery] = useState<SelectedRowIds>(
    getSelectedRowIdsFromState(filters.campaigns?.selectedRowIds ?? [])
  );
  const [lineItemIdsToQuery, setLineItemIdsToQuery] = useState<SelectedRowIds>(
    getSelectedRowIdsFromState(filters.lineItems?.selectedRowIds ?? [])
  );
  const [adsIdsToQuery, setAdsIdsToQuery] = useState<SelectedRowIds>(
    getSelectedRowIdsFromState(filters.ads?.selectedRowIds ?? [])
  );

  const {
    isShowSelectedOnlyEnabled,
    hasSelection: isShowSelectedOnlyAvailable,
    setIsShowSelectedOnlyEnabled,
  } = useShowSelectedOnly();

  // Gated here rather than at the toggle so flag-off query variables stay byte-identical.
  const showSelectedOnly = adsVsmLineItemNavigationLinks && isShowSelectedOnlyEnabled;

  const [drawerProps, setDrawerPropsInternal] = useDrawerProps<TraffickingPageDrawerData>({ data: {} });
  const [modalProps, setModalProps] = useModalProps();
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);

  const openFilterPanel = useCallback(() => {
    setDrawerPropsInternal({ isOpen: false });
    setIsFilterPanelOpen(true);
  }, [setDrawerPropsInternal]);

  const closeFilterPanel = useCallback(() => setIsFilterPanelOpen(false), []);

  const [scrollToFilterId, setScrollToFilterId] = useState<string | null>(null);

  const openFilterPanelToFilter = useCallback(
    (filterId: string) => {
      setDrawerPropsInternal({ isOpen: false });
      setScrollToFilterId(filterId);
      setIsFilterPanelOpen(true);
    },
    [setDrawerPropsInternal]
  );

  const clearScrollToFilterId = useCallback(() => setScrollToFilterId(null), []);

  const setDrawerProps: typeof setDrawerPropsInternal = useCallback(
    (next) => {
      if (next.isOpen) setIsFilterPanelOpen(false);
      setDrawerPropsInternal(next);
    },
    [setDrawerPropsInternal]
  );

  // this flag is used to avoid requests only with selectedRowIds without the actual filter in applyCommonFilter method
  const [skipTableQueries, setSkipTableQueries] = useState(false);

  //  initialState from the URL params is used to initialize local state
  //  selectedRowIds and sortBy are tracked with react-table state

  const [campaignOffset, setCampaignOffset] = useState<number>(0);
  const [lineItemOffset, setLineItemOffset] = useState<number>(0);
  const [adOffset, setAdOffset] = useState<number>(0);

  // Restarts paging and refreshes the query id snapshots, since the result set changes.
  const handleShowSelectedOnlyChange = useCallback(
    (value: boolean): void => {
      setCampaignOffset(0);
      setLineItemOffset(0);
      setAdOffset(0);
      setCampaignIdsToQuery(getSelectedRowIdsFromState(filters?.campaigns?.selectedRowIds ?? []));
      setLineItemIdsToQuery(getSelectedRowIdsFromState(filters.lineItems?.selectedRowIds ?? []));
      setAdsIdsToQuery(getSelectedRowIdsFromState(filters?.ads?.selectedRowIds ?? []));
      setIsShowSelectedOnlyEnabled(value);
    },
    [
      setIsShowSelectedOnlyEnabled,
      filters?.campaigns?.selectedRowIds,
      filters.lineItems?.selectedRowIds,
      filters?.ads?.selectedRowIds,
    ]
  );

  const [campaignSearchTerm, setCampaignSearchTerm] = useState(filters?.campaigns?.searchTerm ?? '');
  const [lineItemSearchTerm, setLineItemSearchTerm] = useState(filters.lineItems?.searchTerm ?? '');
  const [adSearchTerm, setAdSearchTerm] = useState(filters?.ads?.searchTerm ?? '');

  // all campaignIds to render
  const [campaignIds, setCampaignIds] = useState<string[]>([]);
  // variables to manage using only the newest campaignIds (from scrolling) in the gql query
  const prevCampaignIdsRef = useRef(campaignIds);
  const [newCampaignIds, setNewCampaignIds] = useState<string[]>([]);

  // gets the unacknowledgedCampaignIds from the newest campaignIds only (from scrolling)
  const { unacknowledgedCampaignIds } = useGetUnacknowledgedCampaignIds(newCampaignIds);
  // variables to manage all the unacknowledgedCampaignIds, appending the newest ones every time
  const [allUnacknowledgedCampaignIds, setAllUnacknowledgedCampaignIds] = useState<string[]>([]);
  const allUnacknowledgedCampaignIdsRef = useRef<string[]>([]);

  const { filterConfigs, loading: filterConfigLoading } = useGetTraffickingFilterConfig();

  // KATANA-1549: filterConfigs is [] synchronously before getTraffickingFilterConfig resolves,
  // which getAllowedFilterKeys can't distinguish from "loaded empty" — Campaign falls back to fully
  // ungated in that window, forwarding Line-Item/Ad-only persisted fields into CampaignFilterOptionsV5
  // and erroring the whole query. Hold all three table queries until the catalog has loaded.
  const skipUntilFilterConfigLoaded = filterConfigLoading;

  const campaignConfigVars = useMemo(() => getConfigDrivenFilterVars(filterConfigs, filters, TraffickingTab.Campaign), [
    filterConfigs,
    filters,
  ]);
  const lineItemConfigVars = useMemo(() => getConfigDrivenFilterVars(filterConfigs, filters, TraffickingTab.LineItem), [
    filterConfigs,
    filters,
  ]);
  const adsConfigVars = useMemo(() => getConfigDrivenFilterVars(filterConfigs, filters, TraffickingTab.Ad), [
    filterConfigs,
    filters,
  ]);

  const campaignAllowedKeys = useMemo(() => getAllowedFilterKeys(TraffickingTab.Campaign, filterConfigs), [
    filterConfigs,
  ]);
  const lineItemAllowedKeys = useMemo(() => getAllowedFilterKeys(TraffickingTab.LineItem, filterConfigs), [
    filterConfigs,
  ]);
  const adAllowedKeys = useMemo(() => getAllowedFilterKeys(TraffickingTab.Ad, filterConfigs), [filterConfigs]);

  // KATANA-1499 follow-up: same catalog-derived Set for all three tabs — which ids are
  // StringOperatorFilterInput doesn't vary per tab (unlike allowedFilterKeys, which does).
  const stringOperatorKeys = useMemo(() => getStringOperatorFilterKeys(filterConfigs), [filterConfigs]);

  // The filter panel has no date/schedule shortcut — the default "Running This Month" date
  // filter must not be sent as a phantom schedule filter (KATANA-1494). Always use ALL_TIME.
  const effectiveDateFilter = getEffectiveDateFilter(filters.dateFilter);

  // While the toggle is on, each tab's idList is built from its own selection, so all three read the
  // live one — the <tab>IdsToQuery snapshots intentionally lag a checkbox click, which is right for
  // selectedIdList (ordering) but would leave a deselected row on screen here (membership).
  const campaignSelectionForQuery = showSelectedOnly
    ? getSelectedRowIdsFromState(filters?.campaigns?.selectedRowIds ?? [])
    : campaignIdsToQuery;
  const lineItemSelectionForQuery = showSelectedOnly
    ? getSelectedRowIdsFromState(filters.lineItems?.selectedRowIds ?? [])
    : lineItemIdsToQuery;
  const adsSelectionForQuery = showSelectedOnly
    ? getSelectedRowIdsFromState(filters?.ads?.selectedRowIds ?? [])
    : adsIdsToQuery;

  // Campaign is ungated on the legacy path (getAllowedFilterKeys returns null → ?? undefined),
  // preserving today's behavior since CampaignFilterOptionsV5 is the GlobalFilters superset.
  // When the config-driven panel is on it receives the gated set so owner-only filters
  // (applicableQueries excluding Campaign) are not forwarded to the Campaign query.
  const campaignGeneratedVars = generateQueryVariables(
    {
      ...filters,
      dateFilter: effectiveDateFilter,
      searchTerm: campaignSearchTerm,
      sortOption: filters?.campaigns?.sortBy,
      tableName: TraffickingTableName.campaigns,
      selectedRowIds: campaignSelectionForQuery,
      showSelectedOnly,
      selectedCustomFilterId: selectedCustomFilter?.id ?? null,
    },
    undefined,
    undefined,
    campaignAllowedKeys ?? undefined,
    stringOperatorKeys
  );
  const campaignQueryVariables = {
    ...campaignGeneratedVars,
    filterOptions: { ...campaignGeneratedVars.filterOptions, ...campaignConfigVars },
  };

  const lineItemGeneratedVars = generateQueryVariables(
    {
      ...filters,
      dateFilter: effectiveDateFilter,
      searchTerm: lineItemSearchTerm,
      sortOption: filters.lineItems?.sortBy,
      tableName: TraffickingTableName.lineItems,
      selectedRowIds: lineItemSelectionForQuery,
      campaignRowsIds: getSelectedRowIdsFromState(filters?.campaigns?.selectedRowIds ?? []),
      scopeByParentSelection: !adsVsmLineItemNavigationLinks,
      showSelectedOnly,
      selectedCustomFilterId: selectedCustomFilter?.id ?? null,
    },
    undefined,
    undefined,
    lineItemAllowedKeys ?? undefined,
    stringOperatorKeys
  );
  const lineItemQueryVariables = {
    ...lineItemGeneratedVars,
    filterOptions: { ...lineItemGeneratedVars.filterOptions, ...lineItemConfigVars },
  };

  const adsGeneratedVars = generateQueryVariables(
    {
      ...filters,
      dateFilter: effectiveDateFilter,
      searchTerm: adSearchTerm,
      sortOption: filters?.ads?.sortBy,
      tableName: TraffickingTableName.ads,
      selectedRowIds: adsSelectionForQuery,
      campaignRowsIds: getSelectedRowIdsFromState(filters?.campaigns?.selectedRowIds ?? []),
      lineItemRowIds: getSelectedRowIdsFromState(filters.lineItems?.selectedRowIds ?? []),
      scopeByParentSelection: !adsVsmLineItemNavigationLinks,
      showSelectedOnly,
      selectedCustomFilterId: selectedCustomFilter?.id ?? null,
    },
    undefined,
    undefined,
    adAllowedKeys ?? undefined,
    stringOperatorKeys
  );
  const adsQueryVariables = {
    ...adsGeneratedVars,
    filterOptions: { ...adsGeneratedVars.filterOptions, ...adsConfigVars },
  };

  useEffect(() => {
    const newIds = unacknowledgedCampaignIds.filter((id) => !allUnacknowledgedCampaignIdsRef.current.includes(id));
    if (newIds.length > 0) {
      const combined = [...allUnacknowledgedCampaignIdsRef.current, ...newIds];
      allUnacknowledgedCampaignIdsRef.current = combined;
      setAllUnacknowledgedCampaignIds(combined);
    }
  }, [unacknowledgedCampaignIds]);

  const campaignsColumns = useMemo(() => {
    const disabledColumns = [...(traffickingNotifications ? [] : [CampaignColumn.Oms])];

    return getCampaignsColumns({
      hasAccessToChangeLog: permissions?.readTraffickingChangeLog,
      unacknowledgedCampaignIds: allUnacknowledgedCampaignIds,
      disabledColumns,
    });
  }, [traffickingNotifications, permissions?.readTraffickingChangeLog, allUnacknowledgedCampaignIds]);

  const lineItemsColumns = useMemo(() => {
    const disabledColumns = adsPacingStatusAlignment
      ? [LineItemColumn.YodaPacingStatus, LineItemColumn.MetricsUnderPacingRevenue]
      : [];

    return getLineItemsColumns({
      disabledColumns,
      adsPacingStatusAlignment,
      adsPacingStatusAlignmentDefaultHide,
    });
  }, [adsPacingStatusAlignment, adsPacingStatusAlignmentDefaultHide]);
  const adsColumns = useMemo(() => getAdsColumns(), []);

  // Skip conditions are also used to decide which tables to refetch on a post-mutation reload,
  // so a skipped (permission-gated or transiently-skipped) query is never forced onto the network.
  const adsSkip = !permissions?.readTraffickingAds || skipTableQueries || skipUntilFilterConfigLoaded;
  const campaignsSkip = !permissions?.readTraffickingCampaigns || skipTableQueries || skipUntilFilterConfigLoaded;
  const lineItemsSkip = !permissions?.readTraffickingLineItems || skipTableQueries || skipUntilFilterConfigLoaded;

  const adsTable = useTraffickerTable<AdNode, AdModel>(
    GET_ADS,
    adsQueryVariables,
    ResultKeys.ads,
    adNodeToModel,
    adsColumns,
    TraffickingTableName.ads,
    {
      setDrawerProps,
      setModalProps,
      searchTerm: adSearchTerm,
      setSearchTerm: setAdSearchTerm,
      offset: adOffset,
      setOffset: setAdOffset,
      setLineItemOffset,
      setAdOffset,
      setIdsToQuery: setAdsIdsToQuery,
      adsVsmLineItemNavigationLinks,
      skip: adsSkip,
    }
  );

  const campaignsTable = useTraffickerTable<CampaignNode, CampaignModel>(
    GET_CAMPAIGNS,
    campaignQueryVariables,
    ResultKeys.campaigns,
    campaignNodeToModel,
    campaignsColumns,
    TraffickingTableName.campaigns,
    {
      setDrawerProps,
      searchTerm: campaignSearchTerm,
      setSearchTerm: setCampaignSearchTerm,
      offset: campaignOffset,
      setOffset: setCampaignOffset,
      setLineItemOffset: setLineItemOffset,
      setAdOffset: setAdOffset,
      setIdsToQuery: setCampaignIdsToQuery,
      adsVsmLineItemNavigationLinks,
      skip: campaignsSkip,
    }
  );

  useEffect(() => {
    const ids = campaignsTable?.tableInstance?.data?.map((item: CampaignModel) => item.id) ?? [];
    const newIds = ids.filter((id: string) => !prevCampaignIdsRef.current.includes(id));
    setCampaignIds(ids);
    setNewCampaignIds(newIds);
    prevCampaignIdsRef.current = ids;
  }, [campaignsTable?.tableInstance?.data]);

  const lineItemsQuery = adsVsmCampaignInvestigatorUi
    ? GET_LINEITEMS_BY_CAMPAIGNID_LIST_WITH_HEALTH
    : GET_LINEITEMS_BY_CAMPAIGNID_LIST;

  const lineItemsTable = useTraffickerTable<LineItemNode, LineItemModel>(
    lineItemsQuery,
    lineItemQueryVariables,
    ResultKeys.lineItems,
    lineItemNodeToModel,
    lineItemsColumns,
    TraffickingTableName.lineItems,
    {
      setDrawerProps,
      searchTerm: lineItemSearchTerm,
      setSearchTerm: setLineItemSearchTerm,
      offset: lineItemOffset,
      setOffset: setLineItemOffset,
      setLineItemOffset: setLineItemOffset,
      setAdOffset: setAdOffset,
      setIdsToQuery: setLineItemIdsToQuery,
      adsVsmLineItemNavigationLinks,
      skip: lineItemsSkip,
    }
  );

  const onPinLineItem = useCallback(
    (lineItemId: string) => {
      const currentSelectedIds = filters.lineItems?.selectedRowIds ?? [];
      if (!currentSelectedIds.includes(lineItemId)) {
        applyTableFilter('lineItems', 'selectedRowIds', [...currentSelectedIds, lineItemId]);
      }
    },
    [applyTableFilter, filters.lineItems?.selectedRowIds]
  );

  const { scrollTargetId, clearScrollTarget } = useOpenDrawerOnNavigation({
    rows: lineItemsTable.tableInstance.rows,
    initialLoading: lineItemsTable.initialLoading,
    setDrawerProps,
    setLineItemIdsToQuery,
    lineItemFilterOptions: lineItemQueryVariables.filterOptions,
    onPinLineItem,
    toggleRowSelected: lineItemsTable.tableInstance.toggleRowSelected,
  });

  const lineItemNavigationScroll = { scrollTargetId, clearScrollTarget };

  // Refetch every currently-loaded (non-skipped) table so the searcher-applied sort order,
  // pagination, filtering and searcher-only fields are all re-derived after a mutation.
  const reloadTables = useCallback((): Promise<unknown> => {
    const refetches: Array<Promise<unknown>> = [];

    if (!campaignsSkip) refetches.push(campaignsTable.refetch());
    if (!lineItemsSkip) refetches.push(lineItemsTable.refetch());
    if (!adsSkip) refetches.push(adsTable.refetch());

    return Promise.all(refetches);
  }, [adsSkip, campaignsSkip, lineItemsSkip, adsTable, campaignsTable, lineItemsTable]);

  const { scheduleTableReload, isReloading } = useTraffickingTableAutoReload(reloadTables);

  // The post-mutation reload indicator (isReloading) is surfaced separately (see the
  // TraffickingPage overlay) rather than folded into each table's `loading`, so the table
  // stays visible-but-dimmed during the delay + refetch instead of blanking to "loading…".
  const traffickerTables = useMemo(() => {
    return {
      campaigns: campaignsTable,
      lineItems: lineItemsTable,
      ads: adsTable,
    };
  }, [adsTable, lineItemsTable, campaignsTable]);

  const isClearAllBadgeVisible = determineClearAllBadgeVisibility({
    adSearchTerm,
    campaignSearchTerm,
    lineItemSearchTerm,
    traffickerTables,
  });

  const {
    getAssociatedEntities,
    getDescendantEntities,
    getAncestorEntities,
    getParentIdsOfChildren,
  } = useAssociatedEntities({
    lineItemQueryVariables,
    adsQueryVariables,
  });

  /**
   * Removes the given associated entities from the line items and ads selections.
   *
   * Each pair of writes updates react-table state BEFORE the FilterContext, and the order is
   * load-bearing (KATANA-1714). This runs in a promise continuation — clearAssociatedEntities
   * awaits getAssociatedEntities first — so React 17 does not batch, and applyTableFilter's
   * state update flushes synchronously: it re-renders and runs effects before the next
   * statement executes. Writing the FilterContext first therefore opens a window in which any
   * consumer that reads the FilterContext as "previous" and tableInstance.state as "current"
   * sees the rows being removed as freshly *added* instead.
   *
   * useCascadingSelection reads exactly that pair, so with the old order, unchecking a campaign
   * emptied the line-item filter while the table still held those line items, the cascade read
   * the difference as a new line-item selection, and it re-selected upward the very campaign the
   * user had just unchecked. The legacy path masked this because addAssociatedEntities only
   * intersects an existing selection, so a phantom "added" diff there is close to a no-op.
   */
  const unselectAssociatedEntities = useCallback(
    (associatedEntities: Partial<Record<TraffickingTableName, string[]>>, tableName: TraffickingTableName): void => {
      const selectedLineItems = filters?.lineItems?.selectedRowIds ?? [];
      const selectedAds = filters?.ads?.selectedRowIds ?? [];
      const associatedLineItems = associatedEntities.lineItems;
      const associatedAds = associatedEntities.ads ?? [];

      if (associatedLineItems) {
        const newSelectedLineItems = getDifferenceFromSelectedEntities(selectedLineItems, associatedLineItems);

        traffickerTables.lineItems.tableInstance.state.selectedRowIds = getSelectedRowIdsFromState(
          newSelectedLineItems
        );
        applyTableFilter('lineItems', 'selectedRowIds', newSelectedLineItems);
      }
      if (hasSelectedRowIds(TraffickingTableName.ads, traffickerTables)) {
        const newSelectedAds = getDifferenceFromSelectedEntities(selectedAds, associatedAds);

        traffickerTables.ads.tableInstance.state.selectedRowIds = getSelectedRowIdsFromState(newSelectedAds);
        applyTableFilter('ads', 'selectedRowIds', newSelectedAds);
        setAdsIdsToQuery(getSelectedRowIdsFromState(newSelectedAds));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      applyTableFilter,
      filters?.ads?.selectedRowIds,
      filters?.campaigns?.selectedRowIds,
      filters?.lineItems?.selectedRowIds,
      traffickerTables.ads.tableInstance.state,
      traffickerTables.campaigns.tableInstance.state,
      traffickerTables.lineItems.tableInstance.state,
    ]
  );

  const selectAssociatedEntities = useCallback(
    (associatedEntities: Partial<Record<TraffickingTableName, string[]>>, tableName: TraffickingTableName): void => {
      const selectedAds = filters?.ads?.selectedRowIds ?? [];
      const associatedAds = associatedEntities.ads ?? [];
      const areRowIdsSelected = hasSelectedRowIds(tableName, traffickerTables);

      if (areRowIdsSelected) {
        const newSelectedAds = getRepresentedInSelectedEntities(selectedAds, associatedAds);

        // Same ordering rule as unselectAssociatedEntities above: table state first, then the
        // FilterContext, so a synchronous flush can never observe the two disagreeing.
        traffickerTables.ads.tableInstance.state.selectedRowIds = getSelectedRowIdsFromState(newSelectedAds);
        applyTableFilter('ads', 'selectedRowIds', newSelectedAds);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      applyTableFilter,
      filters?.ads?.selectedRowIds,
      traffickerTables.ads.tableInstance.state,
      traffickerTables.campaigns.tableInstance.state,
      traffickerTables.lineItems.tableInstance.state,
    ]
  );

  const clearAssociatedEntities = useCallback(
    async (tableName: TraffickingTableName, prevSelected: string[], newSelected: string[]): Promise<void> => {
      const unselectedIds = getUnselectedIds(prevSelected, newSelected);

      if (unselectedIds.length) {
        const associatedEntities = await getAssociatedEntities(tableName, unselectedIds);

        unselectAssociatedEntities(associatedEntities, tableName);
      }
    },
    [getAssociatedEntities, unselectAssociatedEntities]
  );

  const addAssociatedEntities = useCallback(
    async (tableName: TraffickingTableName, newSelected: string[]): Promise<void> => {
      if (newSelected.length) {
        const associatedEntities = await getAssociatedEntities(tableName, newSelected);

        selectAssociatedEntities(associatedEntities, tableName);
      }
    },
    [getAssociatedEntities, selectAssociatedEntities]
  );

  // KATANA-1714. Reuses the flag KATANA-1420 introduced for selected-rows-to-top, since
  // pinning selections and cascading them are two halves of the same selection rework.
  const isCascadingSelectionEnabled = !!adsVsmLineItemNavigationLinks;

  const selectedRowIdsByTab = useMemo(
    () => ({
      [TraffickingTableName.campaigns]: filters?.campaigns?.selectedRowIds ?? [],
      [TraffickingTableName.lineItems]: filters?.lineItems?.selectedRowIds ?? [],
      [TraffickingTableName.ads]: filters?.ads?.selectedRowIds ?? [],
    }),
    [filters?.campaigns?.selectedRowIds, filters?.lineItems?.selectedRowIds, filters?.ads?.selectedRowIds]
  );

  const setIdsToQueryByTab = useMemo(
    () => ({
      [TraffickingTableName.campaigns]: setCampaignIdsToQuery,
      [TraffickingTableName.lineItems]: setLineItemIdsToQuery,
      [TraffickingTableName.ads]: setAdsIdsToQuery,
    }),
    []
  );

  useCascadingSelection({
    enabled: isCascadingSelectionEnabled,
    traffickerTables,
    selectedRowIdsByTab,
    applyTableFilter,
    setIdsToQueryByTab,
    getDescendantEntities,
    getAncestorEntities,
  });

  useCascadingDeselection({
    enabled: isCascadingSelectionEnabled,
    traffickerTables,
    selectedRowIdsByTab,
    applyTableFilter,
    setIdsToQueryByTab,
    getDescendantEntities,
    getAncestorEntities,
    getParentIdsOfChildren,
  });

  useEffect(() => {
    const campaignEntitiesScope = {
      previous: filters?.campaigns?.selectedRowIds ?? [],
      current: Object.keys(traffickerTables.campaigns.tableInstance.state.selectedRowIds),
    };
    const lineItemsEntitiesScope = {
      previous: filters?.lineItems?.selectedRowIds ?? [],
      current: Object.keys(traffickerTables.lineItems.tableInstance.state.selectedRowIds),
    };
    const updatedDeselectedEntities = getDeselectedEntities(campaignEntitiesScope, lineItemsEntitiesScope);
    const updatedSelectedEntities = getSelectedEntities(campaignEntitiesScope, lineItemsEntitiesScope);

    // Both halves of the legacy path are superseded when the flag is on: useCascadingSelection
    // adds the full family tree rather than intersecting what happens to be selected already,
    // and useCascadingDeselection clears the full subtree rather than only the cached rows.
    // Leaving the legacy deselection running alongside the cascade is what made unchecking a
    // campaign take several clicks — it removed a partial set, and the cascade then reacted to
    // its writes (KATANA-1714).
    if (!isCascadingSelectionEnabled && updatedDeselectedEntities) {
      clearAssociatedEntities(
        updatedDeselectedEntities.tableName,
        updatedDeselectedEntities.previous,
        updatedDeselectedEntities.current
      );
    }
    if (!isCascadingSelectionEnabled && updatedSelectedEntities) {
      addAssociatedEntities(updatedSelectedEntities.tableName, updatedSelectedEntities.current);
    }
  }, [
    addAssociatedEntities,
    clearAssociatedEntities,
    isCascadingSelectionEnabled,
    filters?.campaigns?.selectedRowIds,
    filters?.lineItems?.selectedRowIds,
    traffickerTables.campaigns.tableInstance.state.selectedRowIds,
    traffickerTables.lineItems.tableInstance.state.selectedRowIds,
  ]);

  const clearAdsIdsToQuery = useCallback((): void => {
    setAdsIdsToQuery({});
  }, []);

  const clearAllSearchResultsAndSelections = useCallback((): void => {
    updateOffsetsBasedOnSelectedTab(filters.selectedTab, traffickerTables);

    clearTableFilters();

    clearTablesSelections(traffickerTables);
    clearTableSearchResults(traffickerTables);
    clearAdsIdsToQuery();
  }, [clearAdsIdsToQuery, clearTableFilters, filters, traffickerTables]);

  const applyCommonFilter = useCallback(
    <T extends keyof GlobalFilters>(filterName: T, filterValue: GlobalFilters[T]): void => {
      setSkipTableQueries(true);
      setCampaignOffset(0);
      setLineItemOffset(0);
      setAdOffset(0);
      setCampaignIdsToQuery(getSelectedRowIdsFromState(filters?.campaigns?.selectedRowIds ?? []));
      setLineItemIdsToQuery(getSelectedRowIdsFromState(filters.lineItems?.selectedRowIds ?? []));
      setAdsIdsToQuery(getSelectedRowIdsFromState(filters?.ads?.selectedRowIds ?? []));

      applyFilter(filterName, filterValue);
      setSkipTableQueries(false);
    },
    [applyFilter, filters?.ads?.selectedRowIds, filters?.campaigns?.selectedRowIds, filters.lineItems?.selectedRowIds]
  );

  return {
    drawerProps,
    modalProps,
    applyCommonFilter,
    tables: traffickerTables,
    isClearAllBadgeVisible,
    clearAllSearchResultsAndSelections,
    clearAdsIdsToQuery,
    scheduleTableReload,
    isReloading,
    isFilterPanelOpen,
    openFilterPanel,
    closeFilterPanel,
    scrollToFilterId,
    openFilterPanelToFilter,
    clearScrollToFilterId,
    lineItemNavigationScroll,
    showSelectedOnly: {
      isEnabled: showSelectedOnly,
      isAvailable: adsVsmLineItemNavigationLinks && isShowSelectedOnlyAvailable,
      setIsEnabled: handleShowSelectedOnlyChange,
    },
  };
}

export default useTraffickerState;
